<script setup>
import { computed } from 'vue'
import { useForm, usePage } from '@inertiajs/vue3'

// Tenant list + the active tenant are shared app-wide (sidebar switcher).
const page = usePage()
const tenants = computed(() => page.props.tenants ?? [])

// files + tenants are both arrays: upload many at once, each shared across the
// chosen tenants. Default to the tenant you're acting as (still editable).
const form = useForm({ files: [], tenants: page.props.active_tenant ? [page.props.active_tenant] : [], alt: '' })

const fileCount = computed(() => form.files?.length ?? 0)
// Checkbox items: pick by slug, show the friendly name.
const tenantItems = computed(() => tenants.value.map((t) => ({ label: t.name, value: t.slug })))

// Multipart POST to Rails. Inertia reports native XHR upload progress on
// form.progress, which feeds the UProgress bar below.
const submit = () => form.post('/assets')
</script>

<template>
  <UCard class="mx-auto max-w-lg">
    <template #header>
      <h2 class="font-semibold">Upload images</h2>
    </template>

    <form class="space-y-4" @submit.prevent="submit">
      <UFileUpload
        v-model="form.files"
        multiple
        accept="image/*,video/*"
        label="Drop images or videos here"
        description="Images up to 25 MB · video up to 200 MB"
        class="min-h-40"
      />

      <p v-if="fileCount" class="text-xs text-muted">{{ fileCount }} file{{ fileCount === 1 ? '' : 's' }} selected</p>

      <UFormField label="Tenants" help="Which CMS instances can use these files (applies to the whole batch)" required>
        <UCheckboxGroup v-model="form.tenants" :items="tenantItems" orientation="horizontal" />
      </UFormField>

      <UFormField label="Alt text" help="Optional — applied to every image in the batch; refine per-image later">
        <UInput v-model="form.alt" placeholder="e.g. Land Rover Defender on snow" class="w-full" />
      </UFormField>

      <UAlert
        v-if="form.errors.files"
        color="error"
        variant="soft"
        icon="i-lucide-circle-alert"
        :title="form.errors.files"
      />

      <UAlert
        v-if="form.errors.tenants"
        color="error"
        variant="soft"
        icon="i-lucide-circle-alert"
        :title="form.errors.tenants"
      />

      <UProgress v-if="form.progress" :model-value="form.progress.percentage" />

      <UButton
        type="submit"
        label="Upload"
        icon="i-lucide-upload"
        :loading="form.processing"
        :disabled="!fileCount || !form.tenants.length || form.processing"
      />
    </form>
  </UCard>
</template>
